<template>
  <div>
    <Loader :visible="isLoading" text="Caricamento dati Ordine di Produzione..." />

    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>

    <div class="p-6 space-y-6">
      <p class="text-xl font-semibold text-gray-800">Buono di prelievo</p>
      <!-- Ricerca MO -->
      <div class="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
        <label class="block text-sm font-medium text-gray-700 mb-2">Numero Ordine di Produzione</label>
        <div class="flex flex-wrap items-center gap-2">
          <input
            v-model="moCode"
            type="text"
            placeholder="Inserisci numero ODP"
            class="w-64 p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            @keyup.enter="caricaSqlMo"
          />
          <PrimaryButton @click="caricaSqlMo" :disabled="!moCode">
            <i class="fa fa-search mr-2"></i>Carica ODP
          </PrimaryButton>
          <SecondaryButton v-if="moComponentsLocal && moComponentsLocal.length" @click="reset">
            <i class="fa fa-times mr-2"></i>Reset
          </SecondaryButton>
        </div>
        <p v-if="moError" class="mt-2 text-sm text-red-600">{{ moError }}</p>
      </div>

      <!-- Card info MO -->
      <div v-if="moInfo" class="border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden">
        <div class="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <h3 class="text-sm font-semibold text-gray-700">
            <i class="fa fa-industry mr-2 text-blue-500"></i>
            Dettaglio ODP — <span>{{ moInfo.MONo }}</span>
          </h3>
          <div class="flex items-center gap-2">
            <a
              :href="route('modules.gp.stampaQrOdp', { mo: currentMo })"
              target="_blank"
              rel="noopener"
              class="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-100 whitespace-nowrap"
            >
              <i class="fa fa-qrcode"></i>Stampa QR
            </a>
            <span
              class="text-xs px-2 py-1 rounded font-semibold"
              :class="moStatusClass(moInfo.MOStatus)"
            >{{ moStatusLabel(moInfo.MOStatus) }}</span>
          </div>
        </div>
        <div class="px-4 py-3 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
          <div v-if="moInfo.Job">
            <span class="text-xs text-gray-500 block">Commessa</span>
            <span class="font-semibold text-gray-800 font-mono">{{ moInfo.Job }}</span>
          </div>
          <div v-if="moInfo.InternalOrdNo">
            <span class="text-xs text-gray-500 block">Ordine cliente</span>
            <span class="font-semibold text-gray-800 font-mono">{{ moInfo.InternalOrdNo }}</span>
          </div>
          <div v-if="moInfo.CompanyName">
            <span class="text-xs text-gray-500 block">Cliente</span>
            <span class="font-semibold text-gray-800">{{ moInfo.CompanyName }}</span>
          </div>
          <div v-if="moInfo.ProductionPlanNo">
            <span class="text-xs text-gray-500 block">Piano di produzione</span>
            <span class="font-semibold text-gray-800 font-mono">{{ moInfo.ProductionPlanNo }} / {{ moInfo.ProductionPlanLine }}</span>
          </div>
          <div>
            <span class="text-xs text-gray-500 block">Distinta base</span>
            <span class="font-semibold text-gray-800 font-mono">{{ moInfo.BOM }}</span>
          </div>
          <div>
            <span class="text-xs text-gray-500 block">Qtà da produrre</span>
            <span class="font-semibold text-gray-800">{{ formatQty(moInfo.ProductionQty) }} {{ moInfo.UoM }}</span>
          </div>
          <div>
            <span class="text-xs text-gray-500 block">Qtà prodotta</span>
            <span class="font-semibold text-gray-800">{{ formatQty(moInfo.ProducedQty) }} {{ moInfo.UoM }}</span>
          </div>
          <div>
            <span class="text-xs text-gray-500 block">Data consegna</span>
            <span class="font-semibold text-gray-800">{{ moInfo.DeliveryDate?.substring(0, 10) }}</span>
          </div>
        </div>
        <div v-if="!moPickingAllowed" class="px-4 py-2 bg-red-50 border-t border-red-200 flex items-center gap-2 text-sm text-red-700">
          <i class="fa fa-ban"></i>
          Il prelievo non è consentito: l'ordine di produzione deve essere in stato <strong>Lanciato</strong> o <strong>In lavorazione</strong>.
        </div>
        <div v-if="moInfo.MOStatus == MagoEnums.Production_progress.Created" class="px-4 py-2 bg-amber-50 border-t border-amber-200 flex items-center justify-between gap-2 text-sm">
          <span class="text-amber-700"><i class="fa fa-rocket mr-1"></i>L'ODP è in stato <strong>Creato</strong>: puoi lanciarlo in produzione.</span>
          <button
            @click="lanciaOdp"
            :disabled="isLanciando"
            class="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded bg-green-600 hover:bg-green-700 text-white font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <span v-if="isLanciando" class="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-white"></span>
            <i v-else class="fa fa-rocket"></i>
            {{ isLanciando ? 'Lancio in corso...' : 'Lancia ODP' }}
          </button>
        </div>
      </div>

      <!-- Sezione Fasi ODP (MA_MOSteps) -->
      <div v-if="moStepsLocal && moStepsLocal.length" class="border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden">
        <div class="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
          <h3 class="text-sm font-semibold text-gray-700 flex items-center">
            <i class="fa fa-sitemap mr-2 text-blue-500"></i>
            <span class="inline-flex items-center">
              Fasi ODP — {{ currentMo }}
              <Link
                v-if="canOpenConsuntiva"
                class="inline-flex items-center justify-center w-8 h-8 rounded text-orange-600 hover:bg-orange-100 ml-2"
                :href="route('modules.gp.consuntiva-produzione', { mono: currentMo })"
              >
                <v-icon name="md-precisionmanufacturing-round" title="Apri consuntivazione" />
              </Link>
            </span>
          </h3>
          <span class="text-xs text-gray-500">{{ moStepsLocal.length }} fase/i</span>
        </div>
        <!-- Tabella DESKTOP fasi -->
        <div class="hidden md:block overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">Stato</th>
                <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">Fase</th>
                <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">Operazione</th>
                <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">CdL</th>
                <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">CdL consuntivo</th>
                <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">Tipo Fase</th>
                <th class="px-3 py-2 text-right font-medium text-gray-600 border-b">Attrezzaggio</th>
                <th class="px-3 py-2 text-right font-medium text-gray-600 border-b">Lav. prevista</th>
                <th class="px-3 py-2 text-right font-medium text-gray-600 border-b">Lav. consuntiva</th>
                <th class="px-3 py-2 text-right font-medium text-gray-600 border-b">Coda</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="step in moStepsLocal"
                :key="step.RtgStep"
                :class="[
                  'border-b last:border-b-0 transition hover:bg-blue-50',
                  selectedStep === step.RtgStep ? 'bg-blue-100 font-semibold' : 'bg-white'
                ]"
                class="cursor-pointer"
                @click="toggleStepFilter(step.RtgStep)"
              >
                <td class="px-3 py-2">
                  <span class="inline-block px-2 py-0.5 rounded text-xs font-semibold" :class="moStatusClass(step.MOStatus)">
                    {{ moStatusLabel(step.MOStatus) }}
                  </span>
                </td>
                <td class="px-3 py-2">
                  <span class="inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold"
                    :class="selectedStep === step.RtgStep ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'"
                  >
                    {{ step.RtgStep }}
                  </span>
                </td>
                <td class="px-3 py-2 text-gray-700">{{ step.Operation || '—' }}</td>
                <td class="px-3 py-2 text-gray-800 font-mono text-xs">{{ step.WC }}</td>
                <td class="px-3 py-2 text-gray-600 font-mono text-xs">{{ step.ActualWC || '—' }}</td>
                <td class="px-3 py-2">
                  <span v-if="step.Outsourced == 1" class="inline-block px-2 py-0.5 rounded text-xs bg-orange-100 text-orange-700 font-semibold">Esterna</span>
                  <span v-else class="inline-block px-2 py-0.5 rounded text-xs bg-green-100 text-green-700 font-semibold">Interna</span>
                  <span v-if="step.Outsourced == 1 && step.SupplierName" class="ml-1 text-xs text-gray-600">{{ step.SupplierName }}</span>
                </td>
                <td class="px-3 py-2 text-right text-gray-600">{{ formatTime(step.SetupTime) }}</td>
                <td class="px-3 py-2 text-right text-gray-600">{{ formatTime(step.ProcessingTime) }}</td>
                <td class="px-3 py-2 text-right text-gray-600">{{ step.ActualProcessingTime != null ? formatTime(step.ActualProcessingTime) : '—' }}</td>
                <td class="px-3 py-2 text-right text-gray-600">{{ formatTime(step.QueueTime) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Card MOBILE fasi -->
        <div class="md:hidden divide-y divide-gray-100">
          <div
            v-for="step in moStepsLocal"
            :key="step.RtgStep"
            @click="toggleStepFilter(step.RtgStep)"
            class="p-4 cursor-pointer transition"
            :class="selectedStep === step.RtgStep ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'"
          >
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-2">
                <span
                  class="inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold"
                  :class="selectedStep === step.RtgStep ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'"
                >
                  {{ step.RtgStep }}
                </span>
                <span class="font-semibold text-gray-800 text-sm">{{ step.Operation || '—' }}</span>
              </div>
              <span class="inline-block px-2 py-0.5 rounded text-xs font-semibold" :class="moStatusClass(step.MOStatus)">
                {{ moStatusLabel(step.MOStatus) }}
              </span>
            </div>
            <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
              <span><i class="fa fa-industry mr-1"></i>CdL: <span class="font-mono text-gray-700">{{ step.WC }}</span></span>
              <span v-if="step.ActualWC"><i class="fa fa-check mr-1"></i>Consuntivo: <span class="font-mono text-gray-700">{{ step.ActualWC }}</span></span>
              <span>
                <span v-if="step.Outsourced == 1" class="inline-block px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-semibold">Esterna</span>
                <span v-else class="inline-block px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-semibold">Interna</span>
                <span v-if="step.Outsourced == 1 && step.SupplierName" class="ml-1">{{ step.SupplierName }}</span>
              </span>
            </div>
            <div class="mt-2 grid grid-cols-4 gap-2 text-xs text-center">
              <div class="bg-gray-50 rounded p-1">
                <div class="text-gray-400 mb-0.5">Attrezzaggio</div>
                <div class="font-semibold text-gray-700">{{ formatTime(step.SetupTime) }}</div>
              </div>
              <div class="bg-gray-50 rounded p-1">
                <div class="text-gray-400 mb-0.5">Lav. prevista</div>
                <div class="font-semibold text-gray-700">{{ formatTime(step.ProcessingTime) }}</div>
              </div>
              <div class="bg-gray-50 rounded p-1">
                <div class="text-gray-400 mb-0.5">Lav. consuntiva</div>
                <div class="font-semibold text-gray-700">{{ step.ActualProcessingTime != null ? formatTime(step.ActualProcessingTime) : '—' }}</div>
              </div>
              <div class="bg-gray-50 rounded p-1">
                <div class="text-gray-400 mb-0.5">Coda</div>
                <div class="font-semibold text-gray-700">{{ formatTime(step.QueueTime) }}</div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="selectedStep !== null" class="px-4 py-2 bg-blue-50 border-t border-blue-200 flex items-center justify-between text-sm text-blue-700">
          <span><i class="fa fa-filter mr-1"></i>Filtro attivo: componenti fino alla fase <strong>{{ selectedStep }}</strong></span>
          <button @click="selectedStep = null" class="text-xs underline hover:no-underline">Rimuovi filtro</button>
        </div>
      </div>

      <!-- Sezione Componenti -->
      <div v-if="moComponentsLocal && moComponentsLocal.length" class="border border-gray-200 rounded-lg bg-white shadow-sm overflow-hidden">
        <div class="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
          <h3 class="text-sm font-semibold text-gray-700">
            <i class="fa fa-list mr-2 text-blue-500"></i>
            Componenti — {{ currentMo }}
          </h3>
          <div class="flex items-center gap-3 flex-wrap">
            <SecondaryButton
              @click="stampaStoricoPrelievi"
              class="text-xs"
            >
              <i class="fa fa-print mr-2"></i>Stampa storico prelievi
            </SecondaryButton>
            <SecondaryButton
              v-if="moPickingAllowed"
              @click="saveDeposits"
              :disabled="isSavingDeposits || isSubmitting || isPolling || moComponentsLocal.find(x => x.HasOpenRow == 0 && x.pickings.length == 1 && x.pickings[0].Storage == null)"
              class="text-xs"
            >
              <span v-if="isSavingDeposits" class="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-gray-500 mr-2"></span>
              <i v-else class="fa fa-save mr-2"></i>
              {{ isSavingDeposits ? 'Salvataggio in corso...' : 'Salva depositi' }}
            </SecondaryButton>
            <PrimaryButton
              v-if="hasAnyPrelievo || isPolling || isSubmitting"
              @click="submitPrelievo"
              :disabled="isSubmitting || isPolling || !moPickingAllowed || moComponentsLocal.find(x => x.HasOpenRow == 0 && x.Closed != 1 && parseFloat(x.PickedQuantity) < parseFloat(x.NeededQty))"
              class="text-xs"
            >
              <span v-if="isSubmitting || isPolling" class="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></span>
              <i v-else class="fa fa-paper-plane mr-2"></i>
              {{ isSubmitting ? 'Invio in corso...' : isPolling ? 'In elaborazione...' : 'Invia prelievi' }}
            </PrimaryButton>
            <span v-if="isPolling && pickingStatus !== null" class="text-xs px-2 py-1 rounded font-semibold" :class="pickingStatusClass(pickingStatus)">
              {{ pickingStatusLabel(pickingStatus) }}
            </span>
            <p v-if="submitError" class="text-xs text-red-600">{{ submitError }}</p>
            <p v-if="saveDepositsError" class="text-xs text-red-600">{{ saveDepositsError }}</p>
            <p v-if="pickingStatus === MagoEnums.EOPENMESACTIONSTATUS.Done && !isPolling" class="text-xs text-green-600 font-semibold"><i class="fa fa-check mr-1"></i>Prelievo eseguito</p>
          </div>
          <span class="text-xs text-gray-500">{{ filteredComponents.length }} / {{ moComponentsLocal.length }} righe</span>
        </div>

        <!-- Filtro testo + selettore operatore -->
        <div class="px-4 py-3 border-b border-gray-100 flex flex-wrap items-end gap-4">
          <div class="relative flex-1 min-w-48">
            <input
              v-model="componentSearch"
              type="text"
              placeholder="Filtra per componente, descrizione, codice a barre o lotto..."
              class="w-full p-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              @input="onComponentSearchInput"
            />
            <div class="absolute right-2 top-2.5 text-gray-400 text-xs">
              <i class="fa fa-spinner fa-spin" v-if="componentSearchLoading"></i>
              <i class="fa fa-search" v-else></i>
            </div>
          </div>
          <div v-if="users.length" class="flex flex-col gap-1 min-w-48">
            <label class="text-xs font-medium text-gray-500">Operatore prelievo</label>
            <select
              v-model="selectedUserId"
              class="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option v-for="u in users" :key="u.id" :value="u.id">{{ u.name }}</option>
            </select>
          </div>
        </div>

        <!-- Tabella DESKTOP -->
        <div class="hidden md:block overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">Componente</th>
                <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">Descrizione</th>
                <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">Natura</th>
                <th class="px-3 py-2 text-center font-medium text-gray-600 border-b">Fase min.</th>
                <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">Lotto</th>
                <th class="px-3 py-2 text-right font-medium text-gray-600 border-b">Qtà necessaria</th>
                <th class="px-3 py-2 text-right font-medium text-gray-600 border-b">Qtà prelevata</th>
                <th class="px-3 py-2 text-right font-medium text-gray-600 border-b">Utilizzo</th>
                <th class="px-3 py-2 text-right font-medium text-gray-600 border-b" v-if="moPickingAllowed">Preleva</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(row, index) in filteredComponents" :key="index">
                <tr
                  :class="[
                    'transition',
                    getNotesForComponent(row.Line).length ? '' : 'border-b last:border-b-0',
                    row.NotEnter == 1 ? 'bg-gray-100 text-gray-400' : 'bg-white'
                  ]"
                >
                  <td class="px-3 py-2 font-mono text-xs text-gray-800 font-semibold">{{ row.Component }}</td>
                  <td class="px-3 py-2 text-gray-700 max-w-xs truncate" :title="row.Description">{{ row.Description }}</td>
                  <td class="px-3 py-2">
                    <span v-if="row.Nature != null" class="inline-block px-2 py-0.5 rounded text-xs font-semibold" :class="natureClass(row.Nature)">{{ natureLabel(row.Nature) }}</span>
                    <span v-else class="text-gray-400 text-xs">—</span>
                  </td>
                  <td class="px-3 py-2 text-center">
                    <span
                      v-if="row.DNRtgStep"
                      class="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold"
                      :class="getStepBadgeClass(row.DNRtgStep)"
                      :title="'Componente necessario dalla fase ' + row.DNRtgStep"
                    >
                      {{ row.DNRtgStep }}
                    </span>
                    <span v-else class="text-gray-400 text-xs">—</span>
                  </td>
                  <td class="px-3 py-2 text-xs font-mono">
                    <button
                      v-if="allowLotChange && row.NotEnter != 1 && row.Closed != 1 && parseFloat(row.PickedQuantity) < parseFloat(row.NeededQty) && moPickingAllowed"
                      @click.stop="openLotModal(row)"
                      class="inline-flex items-center gap-1 text-gray-700 hover:text-blue-600 transition"
                      title="Modifica prelievo"
                    >
                      {{ lotsDisplay(row) }}
                      <i class="fa fa-pencil text-gray-400 text-xs"></i>
                    </button>
                    <span v-else>{{ lotsDisplay(row) }}</span>
                  </td>
                  <td class="px-3 py-2 text-right font-semibold text-gray-800">{{ formatQty(row.NeededQty) }} {{ row.UoM }}</td>
                  <td class="px-3 py-2 text-right text-gray-500">
                    <span class="inline-flex items-center gap-1 justify-end">
                      {{ formatQty(row.PickedQuantity) }} {{ row.UoM }}
                      <button
                        v-if="parseFloat(row.PickedQuantity) > 0"
                        @click.stop="openPickingDetails(row)"
                        class="text-blue-400 hover:text-blue-600 transition"
                        title="Dettaglio prelievi"
                      >
                        <i class="fa fa-info-circle"></i>
                      </button>
                    </span>
                  </td>
                  <td class="px-3 py-2 text-right">
                    <div class="flex items-center justify-end gap-2">
                      <div class="w-16 bg-gray-200 rounded-full h-1.5">
                        <div
                          class="h-1.5 rounded-full transition-all"
                          :class="getProgressClass(row)"
                          :style="{ width: Math.min(100, getProgressPercent(row)) + '%' }"
                        ></div>
                      </div>
                      <span class="text-xs w-10 text-right font-semibold" :class="getProgressPercent(row) > 100 ? 'text-red-600' : 'text-gray-500'">{{ getProgressPercent(row) }}%</span>
                    </div>
                  </td>
                  <td class="px-3 py-2 text-right" v-if="moPickingAllowed">
                    <span v-if="row.NotEnter == 1" class="text-gray-400 text-base" title="Non prelevabile"><i class="fa fa-ban"></i></span>
                    <span v-else-if="row.Closed == 1" class="text-gray-400 text-base" title="Chiuso"><i class="fa fa-lock"></i></span>
                    <div v-else class="inline-flex flex-col items-end gap-1">
                      <div
                        v-for="(picking, pi) in row.pickings"
                        :key="pi"
                        class="w-full"
                      >
                        <div class="inline-flex items-center gap-2 justify-end w-full">
                          <span v-if="row.pickings.length > 1" class="text-xs text-gray-500 font-mono">{{ picking.Lot || '—' }}</span>
                          <input
                            type="number"
                            min="0"
                            :step="qtyStep"
                            :max="pickingMaxQty(row, picking) === Infinity ? undefined : pickingMaxQty(row, picking)"
                            v-model.number="picking.qty"
                            :disabled="parseFloat(row.PickedQuantity) >= parseFloat(row.NeededQty) || isPickingBlocked(row, picking)"
                            class="w-20 p-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                            placeholder="0"
                            @click.stop
                            @input="onPickingInput(row, picking)"
                          />
                        </div>
                        <span v-if="row.HasOpenRow == 0 && row.Closed != 1 && parseFloat(row.PickedQuantity) < parseFloat(row.NeededQty)" class="text-xs text-red-500 whitespace-nowrap block text-right">
                          Riga aperta mancante
                        </span>
                        <span v-if="picking.Storage && isPickingBlocked(row, picking)" class="text-xs text-red-500 whitespace-nowrap block text-right">
                          Nessuna disponibilità in {{ picking.Storage }}
                        </span>
                        <span v-else-if="!allowLotOverbook && picking.Storage" class="text-xs text-gray-400 whitespace-nowrap block text-right">
                          Qtà disponibile in {{ picking.Storage }}: {{ formatQty(pickingMaxQty(row, picking)) }} {{ row.UoM }}
                        </span>
                        <span v-if="lotExtraData && (picking.Ubicazione || picking.AdditionalQty1 != null || picking.AdditionalQty2 != null || picking.AdditionalQty3 != null || picking.AdditionalQty4 != null || picking.LotDescription || picking.LotSupplierLotNo)" class="text-[10px] text-gray-400 whitespace-nowrap block text-right">
                          <template v-if="picking.Ubicazione || picking.AdditionalQty1 != null || picking.AdditionalQty2 != null || picking.AdditionalQty3 != null || picking.AdditionalQty4 != null">
                            <template v-if="picking.Ubicazione">{{ picking.Ubicazione }}</template>
                            <template v-if="picking.AdditionalQty1 != null"> – {{ formatLotExtraQty(picking.AdditionalQty1) }} lastre</template>
                            <template v-if="picking.AdditionalQty2 != null || picking.AdditionalQty3 != null || picking.AdditionalQty4 != null"> – {{ formatLotExtraQty(picking.AdditionalQty2) }}x{{ formatLotExtraQty(picking.AdditionalQty3) }}x{{ formatLotExtraQty(picking.AdditionalQty4) }}</template>
                          </template>
                          <template v-else-if="picking.LotDescription">{{ picking.LotDescription }}</template>
                          <template v-if="picking.LotSupplierLotNo"> – L. forn: {{ picking.LotSupplierLotNo }}</template>
                        </span>
                      </div>
                      <label v-if="allowForceClose" class="inline-flex items-center gap-1 text-xs text-gray-500 cursor-pointer whitespace-nowrap" :title="'Chiusura forzata'" @click.stop>
                        <input
                          type="checkbox"
                          v-model="prelievoChiuso[row.Line]"
                          :disabled="parseFloat(row.PickedQuantity) >= parseFloat(row.NeededQty) || chiusuraForzata(row)"
                          class="rounded border-gray-300 text-blue-500 focus:ring-blue-500 disabled:cursor-not-allowed"
                          @click.stop
                        />
                        Ch.
                      </label>
                    </div>
                  </td>
                </tr>
                <tr
                  v-for="(note, ni) in getNotesForComponent(row.Line)"
                  :key="'note-' + index + '-' + ni"
                  :class="[
                    'bg-yellow-50',
                    ni === getNotesForComponent(row.Line).length - 1 ? 'border-b' : ''
                  ]"
                >
                  <td colspan="10" class="px-6 py-1.5">
                    <div class="flex items-start gap-4 text-xs text-gray-600">
                      <span class="shrink-0 text-yellow-600"><i class="fa fa-sticky-note mr-1"></i>Nota {{ note.Line }}</span>
                      <span v-if="note.Notes" class="whitespace-pre-wrap">{{ note.Notes }}</span>
                      <span v-if="note.BOMNotes" class="whitespace-pre-wrap text-gray-500 italic">{{ note.BOMNotes }}</span>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <div v-if="filteredComponents.length === 0" class="px-4 py-8 text-center text-sm text-gray-400">
            <i class="fa fa-inbox text-2xl mb-2 block"></i>
            Nessun componente trovato con i filtri attivi.
          </div>
        </div>

        <!-- Card MOBILE -->
        <div class="md:hidden divide-y divide-gray-100">
          <template v-for="(row, index) in filteredComponents" :key="index">
            <div
              :class="[
                'p-4 transition',
                row.NotEnter == 1 ? 'bg-gray-100 text-gray-400' : 'bg-white'
              ]"
            >
              <div class="flex items-start justify-between mb-1">
                <span class="font-semibold text-gray-800 font-mono text-sm">{{ row.Component }}</span>
                <div class="flex items-center gap-2">
                  <span
                    v-if="row.DNRtgStep"
                    class="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold"
                    :class="getStepBadgeClass(row.DNRtgStep)"
                  >
                    {{ row.DNRtgStep }}
                  </span>
                  <span v-if="row.Nature != null" class="inline-block px-2 py-0.5 rounded text-xs font-semibold" :class="natureClass(row.Nature)">{{ natureLabel(row.Nature) }}</span>
                </div>
              </div>
              <div class="text-sm text-gray-600 mb-2">{{ row.Description }}</div>
              <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                <button
                  v-if="allowLotChange && row.NotEnter != 1 && row.Closed != 1 && parseFloat(row.PickedQuantity) < parseFloat(row.NeededQty) && moPickingAllowed"
                  @click.stop="openLotModal(row)"
                  class="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 transition"
                  title="Modifica"
                >
                  <i class="fa fa-tag mr-1"></i>
                  Modifica prelievo
                  <i class="fa fa-pencil text-xs"></i>
                </button>
                <span v-else-if="row.NotEnter != 1 && lotsDisplay(row) !== '—'" class="inline-flex items-center gap-1 text-gray-600">
                  <i class="fa fa-tag mr-1"></i>
                  Lotto: {{ lotsDisplay(row) }}
                </span>
                <span><i class="fa fa-cubes mr-1"></i>Necessaria: <strong class="text-gray-700">{{ formatQty(row.NeededQty) }} {{ row.UoM }}</strong></span>
                <span class="inline-flex items-center gap-1">
                  <i class="fa fa-check mr-1"></i>Prelevata:
                  <strong class="text-gray-700">{{ formatQty(row.PickedQuantity) }} {{ row.UoM }}</strong>
                  <button
                    v-if="parseFloat(row.PickedQuantity) > 0"
                    @click.stop="openPickingDetails(row)"
                    class="text-blue-400 hover:text-blue-600 transition"
                    title="Dettaglio prelievi"
                  >
                    <i class="fa fa-info-circle"></i>
                  </button>
                </span>
              </div>
              <div class="mt-2">
                <div class="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    class="h-1.5 rounded-full"
                    :class="getProgressClass(row)"
                    :style="{ width: Math.min(100, getProgressPercent(row)) + '%' }"
                  ></div>
                </div>
              </div>
              <div v-if="moPickingAllowed && row.NotEnter == 1" class="mt-2 text-gray-400 text-sm" title="Non prelevabile"><i class="fa fa-ban mr-1"></i>Non prelevabile</div>
              <div v-else-if="moPickingAllowed && row.Closed == 1" class="mt-2 text-gray-400 text-sm" title="Chiuso"><i class="fa fa-lock mr-1"></i>Chiuso</div>
              <div v-else-if="moPickingAllowed" class="mt-2 flex flex-col gap-1" @click.stop>
                <div
                  v-for="(picking, pi) in row.pickings"
                  :key="pi"
                  class="flex items-center gap-2 flex-wrap"
                >
                  <label class="text-xs text-gray-600 shrink-0">
                    Preleva<template v-if="picking.Lot"> (lotto {{ picking.Lot }})</template>:
                  </label>
                  <input
                    type="number"
                    min="0"
                    :step="qtyStep"
                    :max="pickingMaxQty(row, picking) === Infinity ? undefined : pickingMaxQty(row, picking)"
                    v-model.number="picking.qty"
                    :disabled="parseFloat(row.PickedQuantity) >= parseFloat(row.NeededQty) || isPickingBlocked(row, picking)"
                    class="w-24 p-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    placeholder="0"
                    @click.stop
                    @input="onPickingInput(row, picking)"
                  />
                  <span v-if="row.HasOpenRow == 0 && row.Closed != 1 && parseFloat(row.PickedQuantity) < parseFloat(row.NeededQty)" class="text-xs text-red-500 w-full text-right">
                    Riga aperta mancante
                  </span>
                  <span v-if="picking.Storage && isPickingBlocked(row, picking)" class="text-xs text-red-500">
                    Nessuna disponibilità in {{ picking.Storage }}
                  </span>
                  <span v-else-if="!allowLotOverbook && picking.Storage" class="text-xs text-gray-400">
                    Qtà disponibile in {{ picking.Storage }}: {{ formatQty(pickingMaxQty(row, picking)) }} {{ row.UoM }}
                  </span>
                  <span v-if="lotExtraData && (picking.Ubicazione || picking.AdditionalQty1 != null || picking.AdditionalQty2 != null || picking.AdditionalQty3 != null || picking.AdditionalQty4 != null || picking.LotDescription || picking.LotSupplierLotNo)" class="text-[10px] text-gray-400 w-full">
                    <template v-if="picking.Ubicazione || picking.AdditionalQty1 != null || picking.AdditionalQty2 != null || picking.AdditionalQty3 != null || picking.AdditionalQty4 != null">
                      <template v-if="picking.Ubicazione">{{ picking.Ubicazione }}</template>
                      <template v-if="picking.AdditionalQty1 != null"> – {{ formatLotExtraQty(picking.AdditionalQty1) }} lastre</template>
                      <template v-if="picking.AdditionalQty2 != null || picking.AdditionalQty3 != null || picking.AdditionalQty4 != null"> – {{ formatLotExtraQty(picking.AdditionalQty2) }}x{{ formatLotExtraQty(picking.AdditionalQty3) }}x{{ formatLotExtraQty(picking.AdditionalQty4) }}</template>
                    </template>
                    <template v-else-if="picking.LotDescription">{{ picking.LotDescription }}</template>
                    <template v-if="picking.LotSupplierLotNo"> – L. forn: {{ picking.LotSupplierLotNo }}</template>
                  </span>
                </div>
                <label v-if="allowForceClose" class="inline-flex items-center gap-1 text-xs text-gray-500 cursor-pointer" title="Chiusura forzata" @click.stop>
                  <input
                    type="checkbox"
                    v-model="prelievoChiuso[row.Line]"
                    :disabled="parseFloat(row.PickedQuantity) >= parseFloat(row.NeededQty) || chiusuraForzata(row)"
                    class="rounded border-gray-300 text-blue-500 focus:ring-blue-500 disabled:cursor-not-allowed"
                    @click.stop
                  />
                  Chiusura forzata
                </label>
              </div>
            </div>
            <div
              v-for="(note, ni) in getNotesForComponent(row.Line)"
              :key="'note-' + index + '-' + ni"
              class="px-6 py-2 bg-yellow-50 flex items-start gap-3 text-xs text-gray-600"
            >
              <span class="shrink-0 text-yellow-600"><i class="fa fa-sticky-note mr-1"></i>Nota {{ note.Line }}</span>
              <div class="space-y-0.5">
                <p v-if="note.Notes" class="whitespace-pre-wrap">{{ note.Notes }}</p>
                <p v-if="note.BOMNotes" class="whitespace-pre-wrap text-gray-500 italic">{{ note.BOMNotes }}</p>
              </div>
            </div>
          </template>
          <div v-if="filteredComponents.length === 0" class="p-8 text-center text-sm text-gray-400">
            Nessun componente trovato.
          </div>
        </div>
      </div>

      <!-- Stato vuoto iniziale -->
      <div v-if="!moComponentsLocal || !moComponentsLocal.length" class="text-center py-16 text-gray-400">
        <i class="fa fa-search text-4xl mb-3 block"></i>
        <p class="text-sm">Inserisci un numero ODP e premi <strong>Carica ODP</strong> per visualizzare i componenti.</p>
      </div>

    </div>

    <!-- Modal assegnazione lotto -->
    <ConfirmationModal :show="showModal && !!selectedRow" @close="closeModal">
      <template #title>
        Modifica prelievo di <span class="font-mono">{{ selectedRow?.Component }}</span>
      </template>

      <template #content>
        <div v-if="selectedRow" class="space-y-3 text-sm text-gray-700">
          <div class="grid grid-cols-2 gap-2 bg-gray-50 rounded-md p-3">
            <div><span class="font-semibold">Componente:</span> {{ selectedRow.Component }}</div>
            <div><span class="font-semibold">Descrizione:</span> {{ selectedRow.Description }}</div>
            <div><span class="font-semibold">Fase min.:</span> {{ selectedRow.DNRtgStep || '—' }}</div>
            <div><span class="font-semibold">Qtà necessaria:</span> <strong>{{ formatQty(selectedRow.NeededQty) }} {{ selectedRow.UoM }}</strong></div>
          </div>

          <div v-if="lotsLoading" class="flex items-center gap-2 text-gray-500 py-2">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            Caricamento giacenze...
          </div>

          <div v-else-if="lots && lots.length">
            <label class="block text-xs font-medium text-gray-600 mb-1">Seleziona lotti, depositi e quantità</label>
            <div class="space-y-1.5 max-h-64 overflow-y-auto border border-gray-200 rounded-md p-2">
              <div
                v-for="(lot, i) in lots"
                :key="i"
                class="flex items-center gap-2 text-xs"
                :class="lot.blocked == 1 ? 'opacity-50' : ''"
              >
                <input
                  type="checkbox"
                  :checked="(parseFloat(selectedLotQty[i]) || 0) > 0"
                  :disabled="lot.blocked == 1"
                  @change="toggleLot(i)"
                  class="rounded border-gray-300 text-blue-500 focus:ring-blue-500 disabled:cursor-not-allowed"
                />
                <span class="flex-1 font-mono">
                  <span class="font-semibold">{{ lot.Lot }}</span>
                  <span class="text-gray-500"> – {{ lot.Storage }} – disp. {{ formatQty(lot.FinalOnHand) }} {{ selectedRow.UoM }}<template v-if="Number(pickingMaxQty(selectedRow, lot)) !== Number(lot.FinalOnHand)"> (max {{ formatQty(pickingMaxQty(selectedRow, lot)) }})</template></span>
                  <span v-if="lot.PickedByOther" class="text-yellow-600 ml-1"><br>Già assegnato a altre righe odp</span>
                  <span v-if="lot.blocked == 1" class="text-red-600 ml-1">[BLOCCATO]</span>
                  <span v-if="lotExtraData && (lot.Ubicazione || lot.AdditionalQty1 != null || lot.AdditionalQty2 != null || lot.AdditionalQty3 != null || lot.AdditionalQty4 != null || lot.LotDescription || lot.LotSupplierLotNo)" class="text-gray-400 block text-[10px]">
                    <template v-if="lot.Ubicazione || lot.AdditionalQty1 != null || lot.AdditionalQty2 != null || lot.AdditionalQty3 != null || lot.AdditionalQty4 != null">
                      <template v-if="lot.Ubicazione">{{ lot.Ubicazione }}</template>
                      <template v-if="lot.AdditionalQty1 != null"> – {{ formatLotExtraQty(lot.AdditionalQty1) }} lastre</template>
                      <template v-if="lot.AdditionalQty2 != null || lot.AdditionalQty3 != null || lot.AdditionalQty4 != null"> – {{ formatLotExtraQty(lot.AdditionalQty2) }}x{{ formatLotExtraQty(lot.AdditionalQty3) }}x{{ formatLotExtraQty(lot.AdditionalQty4) }}</template>
                    </template>
                    <template v-else-if="lot.LotDescription">{{ lot.LotDescription }}</template>
                    <template v-if="lot.LotSupplierLotNo"> – L. forn: {{ lot.LotSupplierLotNo }}</template>
                  </span>
                </span>
                <input
                  type="number"
                  min="0"
                  :step="qtyStep"
                  :max="lotMaxQty(i) === Infinity ? undefined : lotMaxQty(i)"
                  v-model.number="selectedLotQty[i]"
                  :disabled="lot.blocked == 1"
                  class="w-24 p-1.5 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                  placeholder="0"
                  @input="onLotQtyInput(i)"
                />
              </div>
            </div>
            <div v-if="lots.some(l => l.blocked == 1)" class="mt-1.5 flex items-center gap-1.5 text-xs text-red-600">
              <i class="fa fa-ban"></i>
              Alcune giacenze non sono selezionabili perché appartengono a depositi bloccati per il tuo utente.
            </div>

            <div class="mt-3 text-xs text-gray-500">
              Totale selezionato:
              <strong :class="modalTotalQty > remainingQty(selectedRow) && !(allowOverPick || allowLotOverbook) ? 'text-red-600' : 'text-gray-700'">{{ formatQty(modalTotalQty) }}</strong>
              / necessaria {{ formatQty(selectedRow.NeededQty) }}, prelevata {{ formatQty(selectedRow.PickedQuantity) }} in {{ selectedRow.UoM }}
            </div>

            <label v-if="allowForceClose" class="inline-flex items-center gap-2 mt-3 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                v-model="selectedChiuso"
                :disabled="modalChiusuraForzata || parseFloat(selectedRow.PickedQuantity) >= parseFloat(selectedRow.NeededQty)"
                class="rounded border-gray-300 text-blue-500 focus:ring-blue-500 disabled:cursor-not-allowed"
              />
              Chiusura forzata
            </label>
          </div>

          <div v-else-if="!lotsLoading" class="text-sm text-gray-400 italic py-2">
            Nessuna giacenza per questo componente con i filtri depositi attuali.
          </div>
        </div>
      </template>

      <template #footer>
        <SecondaryButton @click="closeModal">Annulla</SecondaryButton>
        <PrimaryButton
          class="ms-3"
          :disabled="modalTotalQty <= 0"
          @click="cambiaLotto"
        >
          Assegna
        </PrimaryButton>
      </template>
    </ConfirmationModal>

    <!-- Modal dettaglio prelievi -->
    <ConfirmationModal :show="showPickingDetailsModal" @close="showPickingDetailsModal = false" maxWidth="2xl">
      <template #title>
        Dettaglio prelievi — <span class="font-mono">{{ pickingDetailsRow?.Component }}</span>
      </template>

      <template #content>
        <div v-if="pickingDetailsLoading" class="flex items-center gap-2 text-gray-500 py-4">
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          Caricamento...
        </div>
        <div v-else-if="pickingDetailsData && pickingDetailsData.length">
          <!-- Tabella DESKTOP -->
          <div class="hidden md:block overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">Lista prelievo</th>
                  <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">Lotto</th>
                  <th class="px-3 py-2 text-right font-medium text-gray-600 border-b">Qtà prelevata</th>
                  <th class="px-3 py-2 text-left font-medium text-gray-600 border-b">Deposito</th>
                  <th class="px-3 py-2 text-right font-medium text-gray-600 border-b">Costo effettivo</th>
                  <th class="px-3 py-2 text-right font-medium text-gray-600 border-b">Costo previsto</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(detail, i) in pickingDetailsData"
                  :key="i"
                  class="border-b last:border-b-0 hover:bg-gray-50"
                >
                  <td class="px-3 py-2 font-mono text-xs text-gray-700">{{ detail.PickingListNo || '—' }}</td>
                  <td class="px-3 py-2 font-mono text-xs text-gray-700">{{ detail.Lot || '—' }}</td>
                  <td class="px-3 py-2 text-right font-semibold text-gray-800">{{ formatQty(detail.PickedQuantity) }} {{ detail.UoM }}</td>
                  <td class="px-3 py-2 text-xs text-gray-700">{{ detail.Storage || '—' }}</td>
                  <td class="px-3 py-2 text-right text-gray-700">{{ formatCurrency(detail.ActualCost) }}</td>
                  <td class="px-3 py-2 text-right text-gray-700">{{ formatCurrency(detail.BudgetCost) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Card MOBILE -->
          <div class="md:hidden divide-y divide-gray-100 -mx-4 sm:mx-0">
            <div
              v-for="(detail, i) in pickingDetailsData"
              :key="i"
              class="p-3 bg-white"
            >
              <div class="flex items-center justify-between mb-1">
                <span class="font-mono text-xs text-gray-700">{{ detail.PickingListNo || '—' }}</span>
                <span class="font-semibold text-gray-800 text-sm">{{ formatQty(detail.PickedQuantity) }}</span>
              </div>
              <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                <span><i class="fa fa-barcode mr-1"></i>Lotto: <span class="font-mono text-gray-700">{{ detail.Lot || '—' }}</span></span>
                <span><i class="fa fa-warehouse mr-1"></i>Deposito: <span class="text-gray-700">{{ detail.Storage || '—' }}</span></span>
              </div>
              <div class="mt-2 grid grid-cols-2 gap-2 text-xs text-center">
                <div class="bg-gray-50 rounded p-1">
                  <div class="text-gray-400 mb-0.5">Costo effettivo</div>
                  <div class="font-semibold text-gray-700">{{ formatCurrency(detail.ActualCost) }}</div>
                </div>
                <div class="bg-gray-50 rounded p-1">
                  <div class="text-gray-400 mb-0.5">Costo previsto</div>
                  <div class="font-semibold text-gray-700">{{ formatCurrency(detail.BudgetCost) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="py-4 text-sm text-gray-400 italic text-center">
          Nessun dettaglio disponibile.
        </div>
      </template>

      <template #footer>
        <SecondaryButton @click="showPickingDetailsModal = false">Chiudi</SecondaryButton>
      </template>
    </ConfirmationModal>

    <ToastNotification
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      @update:show="toast.show = $event"
    />

  </div>
</template>

<script>
import axios from 'axios';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import Loader from '@/Components/Loader.vue';
import ConfirmationModal from '@/Components/ConfirmationModal.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import ToastNotification from '@/Components/ToastNotification.vue';
import { Link } from '@inertiajs/vue3';

export default {
  components: {
    BreadCrumbs,
    Loader,
    ConfirmationModal,
    PrimaryButton,
    SecondaryButton,
    ToastNotification,
    Link,
  },

  props: {
    canOpenConsuntiva: {
      type: Boolean,
      default: false,
    },
    directLots: {
      type: Boolean,
      default: false,
    },
    allowOverPick: {
      type: Boolean,
      default: false,
    },
    pickCompleteLot: {
      type: Boolean,
      default: false,
    },
    allowLotOverbook: {
      type: Boolean,
      default: false,
    },
    allowForceClose: {
      type: Boolean,
      default: false,
    },
    allowLotChange: {
      type: Boolean,
      default: false,
    },
    users: {
      type: Array,
      default: () => [],
    },
    qtyDecimalPrecision: {
      type: Number,
      default: 2,
    },
    lotExtraData: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      moCode: '',
      currentMo: '',
      isLoading: false,
      moError: null,
      selectedStep: null,
      componentSearch: '',
      componentSearchTimeout: null,
      componentSearchLoading: false,
      searchedPositions: null,
      moInfo: null,
      moComponentsLocal: [],
      moComponentsNotesLocal: [],
      moStepsLocal: [],
      showModal: false,
      selectedRow: null,
      lots: [],
      lotsLoading: false,
      lotsError: null,
      selectedLotQty: {},
      selectedChiuso: false,
      prelievoChiuso: {},
      isLanciando: false,
      isSubmitting: false,
      isPolling: false,
      submitError: null,
      isSavingDeposits: false,
      saveDepositsError: null,
      pickingStatus: null,
      pickingMessage: null,
      toast: { show: false, message: '', type: 'success' },
      showPickingDetailsModal: false,
      pickingDetailsRow: null,
      pickingDetailsData: [],
      pickingDetailsLoading: false,
      selectedUserId: null,
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: this.$t('modules.gestione-produzione'), href: route('modules.home', 'gestione-produzione') },
        { name: this.$t('modules.buono-prelievo'), href: route('modules.gp.prelievo') },
      ],
    };
  },

  computed: {
    filteredComponents() {
      let rows = this.moComponentsLocal || [];

      if (this.selectedStep !== null) {
        rows = rows.filter(r => Number(r.DNRtgStep) <= this.selectedStep);
      }

      if (this.searchedPositions !== null) {
        rows = rows.filter(r => this.searchedPositions.includes(Number(r.Position)));
      }

      return rows;
    },

    hasAnyPrelievo() {
      return (this.moComponentsLocal || []).some(row => this.totalPickedInput(row) > 0);
    },

    qtyStep() {
      return 10 ** -this.quantityPrecision();
    },

    modalTotalQty() {
      const total = Object.values(this.selectedLotQty).reduce((sum, value) => sum + this.roundQty(value), 0);
      return this.roundQty(total);
    },

    modalChiusuraForzata() {
      if (!this.selectedRow) return false;
      const picked  = this.roundQty(this.selectedRow.PickedQuantity);
      const entered = this.modalTotalQty;
      const needed  = this.roundQty(this.selectedRow.NeededQty);
      return needed > 0 && picked + entered >= needed;
    },

    moPickingAllowed() {
      if (!this.moInfo) return false;
      const E = this.MagoEnums.Production_progress;
      const s = Number(this.moInfo.MOStatus);
      return s === E.Released || s === E.Processing;
    },
  },

  mounted() {
    const params = new URLSearchParams(window.location.search);
    const mono = params.get('mono');
    if (mono) {
      this.moCode = mono;
      this.caricaSqlMo();
    }
  },

  methods: {
    async lanciaOdp() {
      this.isLanciando = true;
      try {
        const response = await axios.post(route('modules.gp.lanciaOdp'), { mo: this.currentMo });
        if (response.data?.success === false) {
          this.showToast(response.data.error || 'Errore durante il lancio', 'error');
          return;
        }
        this.showToast('ODP lanciato in produzione con successo', 'success');
        await this.caricaSqlMo();
      } catch (error) {
        this.showToast(error?.response?.data?.error || 'Errore durante il lancio', 'error');
      } finally {
        this.isLanciando = false;
      }
    },

    async caricaSqlMo() {
      if (!this.moCode) {
        this.moError = 'Inserisci un numero di MO';
        return;
      }
      this.moError = null;
      this.isLoading = true;
      this.selectedStep = null;
      this.componentSearch = '';
      this.searchedPositions = null;
      this.currentMo = this.moCode;
      this.prelievoChiuso = {};
      this.submitError    = null;
      this.pickingStatus  = null;
      this.pickingMessage = null;

      if (this.users.length && !this.selectedUserId) {
        this.selectedUserId = this.$page.props.auth.user?.id ?? null;
      }

      try {
        const response = await axios.get(route('modules.gp.getmocomponents'), {
          params: { mo: this.moCode },
        });
        this.moInfo                 = response.data.moInfo            || null;
        this.moComponentsLocal      = (response.data.moComponents || []).map(component => ({
          ...component,
          pickings: (component.pickings || []).map(picking => ({
            ...picking,
            qty: parseFloat(picking.qty) || 0,
          })),
        }));
        this.moComponentsNotesLocal = response.data.moComponentsNotes || [];
        this.moStepsLocal           = response.data.moSteps           || [];
        if (!this.moInfo) {
          this.showToast('Ordine di produzione non trovato', 'error');
          return;
        }
        const initialStatus = response.data.pickingStatus ?? null;
        if (initialStatus) {
          const E = this.MagoEnums.EOPENMESACTIONSTATUS;
          this.pickingStatus  = initialStatus.status;
          this.pickingMessage = initialStatus.message;
          if (initialStatus.status !== null && initialStatus.status !== E.Done && initialStatus.status !== E.Error) {
            await this.pollPickingStatus();
          }
        }
      } catch (error) {
        this.moError = error?.response?.data?.error || 'Errore nel caricamento dell\'ordine di produzione.';
        this.moInfo                 = null;
        this.moComponentsLocal      = [];
        this.moComponentsNotesLocal = [];
        this.moStepsLocal           = [];
      } finally {
        this.isLoading = false;
      }
    },

    reset() {
      this.moCode                 = '';
      this.currentMo              = '';
      this.selectedStep           = null;
      this.componentSearch        = '';
      this.searchedPositions      = null;
      this.moInfo                 = null;
      this.moComponentsLocal      = [];
      this.moComponentsNotesLocal = [];
      this.moStepsLocal           = [];
      this.prelievoChiuso = {};
      this.submitError    = null;
      this.pickingStatus  = null;
      this.pickingMessage = null;
    },

    getNotesForComponent(line) {
      return (this.moComponentsNotesLocal || [])
        .filter(n => n.ComponentLine === line)
        .sort((a, b) => a.Line - b.Line);
    },

    toggleStepFilter(step) {
      this.selectedStep = this.selectedStep === step ? null : step;
    },

    onComponentSearchInput() {
      clearTimeout(this.componentSearchTimeout);
      this.componentSearchTimeout = setTimeout(() => {
        this.performComponentSearch();
      }, 400);
    },

    async performComponentSearch() {
      if (!this.currentMo) return;

      const search = this.componentSearch.trim();
      if (!search) {
        this.searchedPositions = null;
        return;
      }

      this.componentSearchLoading = true;
      try {
        const response = await axios.get(route('modules.gp.searchComponents'), {
          params: { mo: this.currentMo, search },
        });
        this.searchedPositions = response.data.positions || [];

        if (response.data.matchedLot && response.data.matchedPosition != null) {
          const row = this.moComponentsLocal.find(r => Number(r.Position) === Number(response.data.matchedPosition));
          if (row) {
            await this.openLotModal(row, response.data.matchedLot);
          }
        }
      } catch (error) {
        this.showToast(error?.response?.data?.error || 'Errore durante la ricerca del componente', 'error');
      } finally {
        this.componentSearchLoading = false;
      }
    },

    async openLotModal(row, preselectLot = null) {
      this.selectedRow = row;
      this.showModal = true;
      this.lots = [];
      this.selectedLotQty = {};
      this.selectedChiuso = false;
      this.lotsLoading = true;
      this.lotsError = null;

      try {
        const response = await axios.get(route('modules.gp.getlots'), {
          params: { mo_id: this.moInfo.MOId, positions: [row.Position] },
        });
        this.lots = response.data.lots || [];

        this.moComponentsLocal.forEach(otherRow => {
          if (otherRow === row || otherRow.Component !== row.Component) return;

          (otherRow.pickings || []).forEach(picking => {
            const pickedQty = parseFloat(picking.qty) || 0;
            if (pickedQty <= 0) return;

            const lot = this.lots.find(candidate =>
              candidate.Component === otherRow.Component
              && (candidate.Lot || '') === (picking.Lot || '')
              && candidate.Storage === picking.Storage
              && (candidate.Specificator || '') === (picking.Specificator || '')
              && Number(candidate.SpecificatorType || 0) === Number(picking.SpecificatorType || 0)
            );
            if (lot) {
              lot.PickedByOther = true;
            }

            if (lot && lot.FinalOnHand <= 0) {
              this.lots = this.lots.filter(l => l !== lot);
            }
          });
        });

        const currentQty = {};
        (row.pickings || []).forEach(picking => {
          if (!parseFloat(picking.qty)) return;
          const i = this.lots.findIndex(l => l.Lot === picking.Lot && l.Storage === picking.Storage);
          if (i !== -1) {
            currentQty[i] = picking.qty;
          }
        });
        this.selectedLotQty = currentQty;

        if (preselectLot) {
          const lotIndex = this.lots.findIndex(l => l.Lot === preselectLot);
          if (lotIndex !== -1 && !(parseFloat(this.selectedLotQty[lotIndex]) > 0)) {
            this.toggleLot(lotIndex);
          } else if (lotIndex === -1) {
            this.showToast(`Il lotto ${preselectLot} non è selezionabile per questo componente`, 'error');
          }
        }
      } catch (error) {
        this.lotsError = error;
      } finally {
        this.lotsLoading = false;
      }
    },

    closeModal() {
      this.showModal = false;
      this.selectedRow = null;
      this.selectedLotQty = {};
      this.selectedChiuso = false;
    },

    lotMaxQty(i) {
      if (!this.selectedRow || !this.lots[i]) return 0;
      const lot = this.lots[i];
      const remaining = this.remainingQty(this.selectedRow);
      const others = this.roundQty(Object.keys(this.selectedLotQty).reduce((sum, key) => {
        if (Number(key) === i) return sum;
        return sum + this.roundQty(this.selectedLotQty[key]);
      }, 0));
      let max = this.allowOverPick ? Infinity : this.floorQty(Math.max(0, remaining - others));
      if (!this.allowLotOverbook) {
        const avail = this.pickingMaxQty(this.selectedRow, lot);
        max = max === Infinity ? avail : Math.min(max, avail);
      }
      return max;
    },

    onLotQtyInput(i) {
      const max = this.lotMaxQty(i);
      let val = Number(this.selectedLotQty[i]);
      if (!Number.isFinite(val) || val < 0) val = 0;
      if (max !== Infinity && val > max) val = max;
      this.selectedLotQty[i] = this.roundQty(val);

      if (this.allowForceClose && this.modalChiusuraForzata) {
        this.selectedChiuso = true;
      }
    },

    toggleLot(i) {
      const current = parseFloat(this.selectedLotQty[i]) || 0;
      if (current > 0) {
        this.selectedLotQty[i] = 0;
      } else {
        const lot = this.lots[i];
        const others = this.roundQty(Object.keys(this.selectedLotQty).reduce((sum, key) => {
          if (Number(key) === i) return sum;
          return sum + this.roundQty(this.selectedLotQty[key]);
        }, 0));
        const remaining = this.floorQty(Math.max(0, this.remainingQty(this.selectedRow) - others));
        const avail = this.pickingMaxQty(this.selectedRow, lot);
        this.selectedLotQty[i] = this.pickCompleteLot && this.allowOverPick
          ? avail
          : this.floorQty(Math.min(avail, remaining));
      }
    },

    cambiaLotto() {
      if (!this.selectedRow || this.modalTotalQty <= 0) return;

      const idx = this.moComponentsLocal.findIndex(r => r.Line === this.selectedRow.Line);
      if (idx !== -1) {
        const updatedRow = this.moComponentsLocal[idx];
        const pickings = [];

        this.lots.forEach((lot, i) => {
          const qty = parseFloat(this.selectedLotQty[i]) || 0;
          if (qty > 0) {
            pickings.push({
              Lot:              lot.Lot,
              Storage:          lot.Storage,
              Specificator:     lot.Specificator ?? '',
              SpecificatorType: lot.SpecificatorType ?? 0,
              FinalOnHand:      lot.FinalOnHand,
              LotDescription:   lot.LotDescription ?? '',
              LotSupplierLotNo: lot.LotSupplierLotNo ?? '',
              Ubicazione:       lot.Ubicazione ?? '',
              AdditionalQty1:   lot.AdditionalQty1 ?? null,
              AdditionalQty2:   lot.AdditionalQty2 ?? null,
              AdditionalQty3:   lot.AdditionalQty3 ?? null,
              AdditionalQty4:   lot.AdditionalQty4 ?? null,
              
              qty,
            });
          }
        });

        // Recupera le righe già utilizzate da pdp
        for (let i = 0; i < Math.min(updatedRow.pickings?.length || 0, pickings.length); i++) {
          pickings[i].Line = updatedRow.pickings[i].Line;
        }

        // Per le nuove usa da -1 in giù
        for (let i = 0; i < pickings.length - (updatedRow.pickings?.length || 0); i++){
          pickings[updatedRow.pickings?.length || 0 + i].Line = -1;
        }

        if (pickings.length) {
          updatedRow.pickings = pickings;

          if (this.allowForceClose) {
            this.prelievoChiuso[updatedRow.Line] = this.selectedChiuso || this.modalChiusuraForzata;
          }
        }
      }

      this.closeModal();
    },

    async submitPrelievo() {
      const assignments = [];
      for (const row of this.moComponentsLocal) {
        const activePickings = (row.pickings || []).filter(p => parseFloat(p.qty) > 0);
        activePickings.forEach((picking, pi) => {
          const assignment = {
            position:         row.Position,
            pickedQty:        picking.qty,
            storage:          picking.Storage || '',
            specificator:     picking.Specificator || '',
            specificatorType: picking.SpecificatorType ?? 0,
            lot:              picking.Lot || '',
            neededQty:        parseFloat(row.NeededQty) || 0,
            component:        row.Component,
          };
          if (this.allowForceClose && pi === activePickings.length - 1) {
            assignment.closed = !!this.prelievoChiuso[row.Line];
          }
          assignments.push(assignment);
        });
      }

      if (!assignments.length) return;

      this.isSubmitting   = true;
      this.submitError    = null;
      this.pickingStatus  = null;
      this.pickingMessage = null;

      try {
        const response = await axios.post(route('modules.gp.executePickings'), {
          mo: this.currentMo,
          assignments,
          ...(this.selectedUserId ? { user_id: this.selectedUserId } : {}),
        });
        if (response.data?.success === false) {
          this.submitError = response.data?.error || 'Errore durante il prelievo';
          return;
        }
        await this.pollPickingStatus();
      } catch (error) {
        this.submitError = error?.response?.data?.error || 'Errore durante il prelievo';
      } finally {
        this.isSubmitting = false;
      }
    },

    async saveDeposits() {
      const positions = [];
      for (const row of this.moComponentsLocal) {
        const deposits = (row.pickings || [])
          .filter(p => p.Storage)
          .map(p => ({
            storage:          p.Storage,
            specificator:     p.Specificator || '',
            specificatorType: p.SpecificatorType ?? 0,
            lot:              p.Lot || '',
            qty:              p.qty ?? 0,
            line:             p.Line,
          }));

        if (deposits.length) {
          positions.push({ position: row.Position, deposits });
        }
      }

      if (!positions.length) return;

      this.isSavingDeposits = true;
      this.saveDepositsError = null;

      try {
        const response = await axios.post(route('modules.gp.saveDeposits'), {
          mo: this.currentMo,
          positions,
        });
        if (response.data?.success === false) {
          this.saveDepositsError = response.data?.error || 'Errore durante il salvataggio dei depositi';
          return;
        }
        await this.caricaSqlMo();
        this.showToast('Depositi salvati con successo', 'success');
      } catch (error) {
        this.saveDepositsError = error?.response?.data?.error || 'Errore durante il salvataggio dei depositi';
      } finally {
        this.isSavingDeposits = false;
      }
    },

    stampaStoricoPrelievi() {
      if (!this.currentMo) return;
      const url = route('modules.gp.stampaStoricoPrelievi', { mo: this.currentMo });
      window.open(url, '_blank');
    },

    async pollPickingStatus() {
      this.isPolling = true;
      const maxAttempts = 30;

      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, 2000));

        try {
          const res = await axios.get(route('modules.gp.pickingStatus'), {
            params: { mo: this.currentMo },
          });

          this.pickingStatus  = res.data.status;
          this.pickingMessage = res.data.message;

          const E = this.MagoEnums.EOPENMESACTIONSTATUS;
          if (res.data.status === E.Done) {
            this.showToast('Prelievo eseguito con successo', 'success');
            this.prelievoChiuso = {};
            await this.caricaSqlMo();
            break;
          }
          if (res.data.status === E.Error) {
            this.submitError = res.data.message || 'Errore nel prelievo';
            break;
          }
        } catch {
          break;
        }
      }

      this.isPolling = false;
    },

    pickingStatusLabel(status) {
      if (status === null) return '';
      const E = this.MagoEnums.EOPENMESACTIONSTATUS;
      const map = {
        [E.To_Do]:        'In attesa',
        [E.In_Processing]:'In elaborazione',
        [E.Done]:         'Completato',
        [E.Error]:        'Errore',
        [E.Wip]:          'In corso',
        [E.Hold]:         'In sospeso',
      };
      return map[status] ?? String(status);
    },

    pickingStatusClass(status) {
      const E = this.MagoEnums.EOPENMESACTIONSTATUS;
      if (status === E.Done)                            return 'bg-green-100 text-green-700';
      if (status === E.Error)                           return 'bg-red-100 text-red-700';
      if (status === E.In_Processing || status === E.Wip) return 'bg-yellow-100 text-yellow-700';
      return 'bg-gray-100 text-gray-600';
    },

    showToast(message, type = 'success') {
      this.toast.message = message;
      this.toast.type    = type;
      this.toast.show    = true;
    },

    quantityPrecision() {
      return Math.max(0, Math.min(10, Math.trunc(Number(this.qtyDecimalPrecision) || 0)));
    },

    roundQty(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return number === Infinity ? Infinity : 0;
      const precision = this.quantityPrecision();
      const factor = 10 ** precision;
      return Number((Math.round((number + Number.EPSILON) * factor) / factor).toFixed(precision));
    },

    floorQty(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return number === Infinity ? Infinity : 0;
      const precision = this.quantityPrecision();
      const factor = 10 ** precision;
      return Number((Math.floor(number * factor + Number.EPSILON) / factor).toFixed(precision));
    },

    remainingQty(row) {
      const needed = Number(row.NeededQty) || 0;
      const picked = Number(row.PickedQuantity) || 0;
      return this.floorQty(Math.max(0, needed - picked));
    },

    totalPickedInput(row) {
      const total = (row.pickings || []).reduce((sum, picking) => sum + this.roundQty(picking.qty), 0);
      return this.roundQty(total);
    },

    lotsDisplay(row) {
      return row.pickings.length > 1 ? 'Multipli' : (row.pickings[0]?.Lot || '—');
    },

    pickingMaxQty(row, picking) {
      if (this.allowLotOverbook)
        return Infinity;

      let max = Number(picking.FinalOnHand) || 0;

      this.moComponentsLocal.filter(x => x != row).forEach(otherRow => {
          if (otherRow.Component !== row.Component) return;

          otherRow.pickings.forEach(x => {
            if (x.Lot === picking.Lot
              && x.Storage === picking.Storage
              && (x.Specificator || '') === (picking.Specificator || '')
              && Number(x.SpecificatorType || 0) === Number(picking.SpecificatorType || 0)
            ) {
              max -= this.roundQty(x.qty);
            }
          })
      });
      return this.floorQty(Math.max(0, max));
    },

    isPickingBlocked(row, picking) {
      return !this.allowLotOverbook && !!picking.Storage && this.pickingMaxQty(row, picking) <= 0;
    },

    onPickingInput(row, picking) {
      const max = this.pickingMaxQty(row, picking);
      let val = Number(picking.qty);
      if (!Number.isFinite(val) || val < 0) val = 0;
      if (max !== Infinity && val > max) val = max;
      picking.qty = this.roundQty(val);

      if (this.allowForceClose && this.chiusuraForzata(row)) {
        this.prelievoChiuso[row.Line] = true;
      }
    },

    chiusuraForzata(row) {
      const picked  = this.roundQty(row.PickedQuantity);
      const entered = this.totalPickedInput(row);
      const needed  = this.roundQty(row.NeededQty);
      return needed > 0 && picked + entered >= needed;
    },

    getProgressPercent(row) {
      const needed = parseFloat(row.NeededQty) || 0;
      if (needed === 0) return 100;
      const picked = parseFloat(row.PickedQuantity) || 0;
      return Math.round((picked / needed) * 100);
    },

    getProgressClass(row) {
      const pct = this.getProgressPercent(row);
      if (pct > 100)  return 'bg-red-500';
      if (pct === 100) return 'bg-green-500';
      if (pct > 0)    return 'bg-blue-500';
      return 'bg-gray-300';
    },

    getStepBadgeClass(step) {
      const colors = [
        'bg-blue-200 text-blue-800',
        'bg-purple-200 text-purple-800',
        'bg-orange-200 text-orange-800',
        'bg-teal-200 text-teal-800',
        'bg-pink-200 text-pink-800',
        'bg-indigo-200 text-indigo-800',
      ];
      const idx = (Number(step) - 1) % colors.length;
      if (this.selectedStep === Number(step)) return 'bg-blue-500 text-white';
      return colors[Math.max(0, idx)];
    },

    natureLabel(nature) {
      const E = this.MagoEnums.Item_Kind;
      const map = {
        [E.Semifinished]: 'Semilavorato',
        [E.Finished_Product]: 'Prodotto finito',
        [E.Purchase]: 'Acquisto',
      };
      return map[nature] ?? '—';
    },

    natureClass(nature) {
      const E = this.MagoEnums.Item_Kind;
      const map = {
        [E.Semifinished]: 'bg-purple-100 text-purple-700',
        [E.Finished_Product]: 'bg-green-100 text-green-700',
        [E.Purchase]: 'bg-orange-100 text-orange-700',
      };
      return map[nature] ?? 'bg-gray-100 text-gray-500';
    },

    moStatusLabel(status) {
      const E = this.MagoEnums.Production_progress;
      const map = {
        [E.Released]: 'Lanciato',
        [E.Processing]: 'In lavorazione',
        [E.Confirmed]: 'Terminato',
        [E.Created]: 'Creato',
        [E.Proposed_by_MRP]: 'Proposto da MRP',
        [E.Planned_by_CRP]: 'Pianificato da CRP',
        [E.Proposed_by_MRPMinusCRP]: 'Proposto MRP-CRP',
      };
      return map[status] ?? '—';
    },

    moStatusClass(status) {
      const E = this.MagoEnums.Production_progress;
      const map = {
        [E.Released]: 'bg-blue-100 text-blue-700',
        [E.Processing]: 'bg-yellow-100 text-yellow-700',
        [E.Confirmed]: 'bg-green-100 text-green-700',
        [E.Created]: 'bg-gray-100 text-gray-600',
        [E.Proposed_by_MRP]: 'bg-purple-100 text-purple-700',
        [E.Planned_by_CRP]: 'bg-indigo-100 text-indigo-700',
        [E.Proposed_by_MRPMinusCRP]: 'bg-pink-100 text-pink-700',
      };
      return map[status] ?? 'bg-gray-100 text-gray-500';
    },

    async openPickingDetails(row) {
      this.pickingDetailsRow    = row;
      this.pickingDetailsData   = [];
      this.pickingDetailsLoading = true;
      this.showPickingDetailsModal = true;

      try {
        const response = await axios.get(route('modules.gp.pickingHistory'), {
          params: { mo: this.currentMo, position: row.Position },
        });
        this.pickingDetailsData = response.data.details || [];
      } catch {
        this.pickingDetailsData = [];
      } finally {
        this.pickingDetailsLoading = false;
      }
    },

    formatTime(val) {
      if (val === null || val === undefined || val === '') return '—';
      const totalSeconds = Number(val);
      if (isNaN(totalSeconds)) return '—';
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      return `${hours}:${String(minutes).padStart(2, '0')}`;
    },

    formatLotExtraQty(val) {
      if (val === null || val === undefined || val === '') return '';
      const normalized = String(val).trim().replace(',', '.');
      if (!/^-?\d+(?:\.\d+)?$/.test(normalized)) return String(val);
      const [integer, fraction = ''] = normalized.split('.');
      const trimmedFraction = fraction.replace(/0+$/, '');
      return trimmedFraction ? `${integer},${trimmedFraction}` : integer;
    },

    formatQty(val) {
      if (val === null || val === undefined || val === '') return '—';
      const n = Number(val);
      if (isNaN(n)) return '—';
      return n.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: this.qtyDecimalPrecision });
    },
  },

};
</script>
